import React, { useRef, useEffect } from 'react';
import { Card, Button, ProgressBar, Alert, Badge, ListGroup } from 'react-bootstrap';
import { 
  FaUpload, 
  FaTrash, 
  FaFile, 
  FaFilePdf, 
  FaFileWord, 
  FaFileExcel, 
  FaFileImage,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner
} from 'react-icons/fa';
import useFileUpload from '../../hooks/useFileUpload';
import './FileUploader.css';

/**
 * FileUploader Component
 * UI-only component for file upload with drag-and-drop support
 */
const FileUploader = ({ 
  reportId = null,
  maxFiles = 5,
  maxSize = 10 * 1024 * 1024, // 10MB
  onUploadSuccess = null,
  onUploadError = null,
  onDeleteSuccess = null,
  disabled = false,
  autoUpload = false, // Auto-upload on file selection
  showUploadButton = true, // Show manual upload button
  initialFiles = [] // Pre-loaded uploaded files
}) => {
  const fileInputRef = useRef(null);

  const {
    files,
    uploading,
    progress,
    errors,
    uploadedFiles,
    allUploaded,
    addFiles,
    removeFile,
    uploadAll,
    deleteFile
  } = useFileUpload(reportId, {
    maxFiles,
    maxSize,
    onUploadSuccess,
    onUploadError,
    onDeleteSuccess
  });

  // Track if we've already initiated an upload for the current set of files
  const uploadingRef = useRef(false);
  const pendingCountRef = useRef(0);
  
  // Auto-upload when files are added and autoUpload is enabled
  useEffect(() => {
    if (!autoUpload) return;
    
    const pendingFiles = files.filter(f => f.status === 'pending');
    const currentPendingCount = pendingFiles.length;
    
    // Only trigger upload if:
    // 1. We have pending files
    // 2. Not already uploading
    // 3. The pending count increased (new files were added)
    if (currentPendingCount > 0 && 
        !uploadingRef.current && 
        currentPendingCount > pendingCountRef.current) {
      console.log('🎯 useEffect detected NEW pending files, triggering upload');
      console.log('🎯 Previous pending count:', pendingCountRef.current, 'Current:', currentPendingCount);
      
      uploadingRef.current = true;
      pendingCountRef.current = currentPendingCount;
      
      uploadAll().finally(() => {
        uploadingRef.current = false;
        // Reset count after upload completes
        pendingCountRef.current = 0;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, autoUpload]); // Only react to files and autoUpload changes, not uploadAll function reference

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    console.log('📎 File input changed, files:', selectedFiles.length);
    
    if (selectedFiles && selectedFiles.length > 0) {
      const added = addFiles(selectedFiles);
      console.log('📎 Files added to queue:', added);
    }
    
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled || uploading) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      addFiles(droppedFiles);
      // useEffect will handle auto-upload
    }
  };

  // Trigger file input click
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Get file icon based on type
  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <FaFilePdf className="text-danger" />;
    if (type.includes('word') || type.includes('document')) return <FaFileWord className="text-primary" />;
    if (type.includes('excel') || type.includes('sheet')) return <FaFileExcel className="text-success" />;
    if (type.includes('image')) return <FaFileImage className="text-info" />;
    if (type.includes('text')) return <FaFileAlt className="text-secondary" />;
    return <FaFile className="text-muted" />;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>;
      case 'uploading':
        return <Badge bg="info"><FaSpinner className="spinner" /> Uploading</Badge>;
      case 'success':
        return <Badge bg="success"><FaCheckCircle /> Uploaded</Badge>;
      case 'error':
        return <Badge bg="danger"><FaExclamationCircle /> Failed</Badge>;
      default:
        return null;
    }
  };

  // Calculate total files (pending + initial files only, since parent manages uploaded state)
  const pendingFiles = files.filter(f => f.status !== 'success');
  const totalFiles = pendingFiles.length + initialFiles.length;
  const canAddMore = totalFiles < maxFiles;

  return (
    <Card className="file-uploader-card">
      <Card.Body>
        <div className="file-uploader">
          {/* Drop Zone */}
          <div
            className={`upload-drop-zone ${!canAddMore ? 'disabled' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
              onChange={handleFileChange}
              disabled={disabled || uploading || !canAddMore}
              style={{ display: 'none' }}
            />
            
            <div className="upload-icon">
              <FaUpload size={40} />
            </div>
            
            <h5>Drop files here or click to browse</h5>
            <p className="text-muted">
              Accepted: PDF, Word, Excel, Images, Text
              <br />
              Max size: {(maxSize / (1024 * 1024)).toFixed(0)}MB per file
              <br />
              {totalFiles} of {maxFiles} files
            </p>
            
            <Button
              variant="primary"
              onClick={handleBrowseClick}
              disabled={disabled || uploading || !canAddMore}
            >
              <FaUpload className="me-2" />
              Browse Files
            </Button>
          </div>

          {/* Error Messages */}
          {Object.keys(errors).length > 0 && (
            <Alert variant="danger" className="mt-3">
              <strong>Upload Errors:</strong>
              <ul className="mb-0 mt-2">
                {Object.entries(errors).map(([key, error]) => (
                  <li key={key}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Files List - Only show pending, uploading, or error files */}
          {files.filter(f => f.status !== 'success').length > 0 && (
            <div className="files-list mt-4">
              <h6 className="mb-3">Selected Files ({files.filter(f => f.status !== 'success').length})</h6>
              <ListGroup>
                {files.filter(f => f.status !== 'success').map((fileObj) => (
                  <ListGroup.Item key={fileObj.id} className="file-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="file-icon me-3">
                          {getFileIcon(fileObj.type)}
                        </div>
                        
                        <div className="file-info flex-grow-1">
                          <div className="file-name">{fileObj.name}</div>
                          <small className="text-muted">{fileObj.sizeFormatted}</small>
                          
                          {/* Progress Bar */}
                          {fileObj.status === 'uploading' && progress[fileObj.id] !== undefined && (
                            <ProgressBar
                              now={progress[fileObj.id]}
                              label={`${progress[fileObj.id]}%`}
                              variant="info"
                              className="mt-2"
                              style={{ height: '20px' }}
                            />
                          )}
                          
                          {/* Error Message */}
                          {fileObj.status === 'error' && errors[fileObj.name] && (
                            <small className="text-danger d-block mt-1">
                              {errors[fileObj.name]}
                            </small>
                          )}
                        </div>
                        
                        <div className="file-status ms-3">
                          {getStatusBadge(fileObj.status)}
                        </div>
                      </div>
                      
                      {/* Remove Button */}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-3"
                        onClick={() => removeFile(fileObj.id)}
                        disabled={fileObj.status === 'uploading'}
                        title="Remove file"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {/* Upload All Button */}
              {showUploadButton && !autoUpload && files.some(f => f.status === 'pending') && (
                <Button
                  variant="success"
                  className="mt-3 w-100"
                  onClick={uploadAll}
                  disabled={uploading || disabled}
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="spinner me-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FaUpload className="me-2" />
                      Upload All Files
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {/* Uploaded Files (from initial files only - parent manages state) */}
          {initialFiles.length > 0 && (
            <div className="uploaded-files mt-4">
              <h6 className="mb-3 text-success">
                <FaCheckCircle className="me-2" />
                Uploaded Documents ({initialFiles.length})
              </h6>
              <ListGroup>
                {initialFiles.map((file, index) => (
                  <ListGroup.Item key={index} className="file-item">
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center flex-grow-1">
                        <div className="file-icon me-3">
                          {getFileIcon(file.type || 'file')}
                        </div>
                        
                        <div className="file-info">
                          <div className="file-name">{file.name}</div>
                          {file.size && (
                            <small className="text-muted">
                              {typeof file.size === 'number' 
                                ? `${(file.size / 1024).toFixed(2)} KB`
                                : file.size
                              }
                            </small>
                          )}
                        </div>
                      </div>
                      
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => deleteFile(file.path)}
                        disabled={uploading}
                        title="Delete file"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </div>
          )}

          {/* Success Message */}
          {allUploaded && files.length > 0 && (
            <Alert variant="success" className="mt-3">
              <FaCheckCircle className="me-2" />
              All files uploaded successfully!
            </Alert>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default FileUploader;
